import type { CSSProperties } from 'vue';

type LandingParticleStyleOptions = {
    sizeMin?: number;
    sizeMax?: number;
    durationMinSeconds?: number;
    durationMaxSeconds?: number;
    delayMinSeconds?: number;
    delayMaxSeconds?: number;
    opacityMin?: number;
    opacityMax?: number;
};

const toSeedInt = (value: number, scale = 1000) => Math.round(value * scale) | 0;

const fnv1a = (ints: number[]) => {
    let hash = 0x811c9dc5;
    for (const i of ints) {
        hash ^= i;
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
};

const mulberry32 = (seed: number) => {
    let t = seed >>> 0;
    return () => {
        t = (t + 0x6d2b79f5) >>> 0;
        let x = Math.imul(t ^ (t >>> 15), t | 1);
        x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
};

const range = (rand: () => number, min: number, max: number) => rand() * (max - min) + min;

export const createLandingParticleStyles = (count: number, options: LandingParticleStyleOptions = {}): CSSProperties[] => {
    const {
        sizeMin = 2,
        sizeMax = 5,
        durationMinSeconds = 8,
        durationMaxSeconds = 20,
        delayMinSeconds = 0,
        delayMaxSeconds = 5,
        opacityMin = 0.3,
        opacityMax = 0.7,
    } = options;

    const baseSeed = fnv1a([
        count | 0,
        toSeedInt(sizeMin),
        toSeedInt(sizeMax),
        toSeedInt(durationMinSeconds),
        toSeedInt(durationMaxSeconds),
        toSeedInt(delayMinSeconds),
        toSeedInt(delayMaxSeconds),
        toSeedInt(opacityMin),
        toSeedInt(opacityMax),
    ]);

    return Array.from({ length: count }, (_, index) => {
        const rand = mulberry32((baseSeed + Math.imul(index + 1, 0x9e3779b9)) >>> 0);
        const size = range(rand, sizeMin, sizeMax);
        return {
            left: `${range(rand, 0, 100)}%`,
            top: `${range(rand, 0, 100)}%`,
            width: `${size}px`,
            height: `${size}px`,
            animationDuration: `${range(rand, durationMinSeconds, durationMaxSeconds)}s`,
            animationDelay: `${range(rand, delayMinSeconds, delayMaxSeconds)}s`,
            opacity: range(rand, opacityMin, opacityMax),
        };
    });
};
